import { useState } from "react";
import { Send, Loader2, CheckCircle2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { createCommunitySubmission, uploadImage, mediaUrl } from "@/lib/api";
import { AdSlot } from "@/components/AdSlot";

const CATS = ["music", "athletes", "fashion", "shoes", "cars", "travel", "technology", "business", "media", "food"];
const TYPES = [
  { id: "story", label: "My Story" },
  { id: "style", label: "My Style / Fit" },
  { id: "photo", label: "Photo Feature" },
  { id: "tip", label: "News Tip" },
];

export default function Community() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    handle: "",
    submission_type: "story",
    category: "music",
    message: "",
    image: null,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      set("image", url);
      toast.success("Photo attached.");
    } catch {
      toast.error("Upload failed. Try a smaller image (max 8MB).");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and your message are required.");
      return;
    }
    setSaving(true);
    try {
      await createCommunitySubmission(form);
      setDone(true);
      toast.success("Submission received. We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
  const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16">
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Community</span>
      <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="community-title">
        Get Featured
      </h1>
      <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
        NERO SUN is built by the culture, for the culture. Pitch your story, drop your
        fit, send a photo feature or a hot tip — the best submissions get spotlighted.
      </p>

      <AdSlot variant="leaderboard" slot={process.env.REACT_APP_ADSENSE_SLOT_COMMUNITY} className="mt-8" />

      {done ? (
        <div className="border border-rankup/40 bg-rankup/10 p-8 mt-10 text-center" data-testid="community-success">
          <CheckCircle2 className="w-10 h-10 text-rankup mx-auto" />
          <h2 className="font-anton text-2xl uppercase tracking-tight mt-4">You're in the pile</h2>
          <p className="text-muted-foreground mt-2">
            Thanks, {form.name.split(" ")[0]}. Our editors review every submission. If it's a fit,
            we'll reach out at {form.email}.
          </p>
          <button
            data-testid="community-again-btn"
            onClick={() => {
              setDone(false);
              setForm({ name: "", email: "", handle: "", submission_type: "story", category: "music", message: "", image: null });
            }}
            className="mt-6 font-mono text-xs uppercase tracking-widest border border-white/15 px-5 py-2.5 hover:border-signal hover:text-signal transition-colors"
          >
            Submit another
          </button>
        </div>
      ) : (
        <div className="border border-white/10 bg-card p-6 md:p-8 mt-10 space-y-5">
          <div>
            <label className={label}>What are you submitting?</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`community-type-${t.id}`}
                  onClick={() => set("submission_type", t.id)}
                  className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                    form.submission_type === t.id ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={label}>Name</label>
              <input data-testid="community-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={inputCls} />
            </div>
            <div>
              <label className={label}>Email</label>
              <input data-testid="community-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className={inputCls} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={label}>Social handle (optional)</label>
              <input data-testid="community-handle" value={form.handle} onChange={(e) => set("handle", e.target.value)} placeholder="@yourhandle" className={inputCls} />
            </div>
            <div>
              <label className={label}>Section</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CATS.map((c) => (
                  <button
                    key={c}
                    data-testid={`community-cat-${c}`}
                    onClick={() => set("category", c)}
                    className={`font-mono text-xs uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                      form.category === c ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className={label}>Tell us more</label>
            <textarea
              data-testid="community-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={5}
              placeholder="Your story, why it matters, links, anything we should know…"
              className={inputCls}
            />
          </div>

          <div>
            <label className={label}>Attach a photo (optional)</label>
            {form.image ? (
              <div className="mt-2 relative w-full max-w-xs">
                <img src={mediaUrl(form.image)} alt="Submission" className="w-full h-40 object-cover border border-white/15" />
                <button
                  data-testid="community-remove-photo"
                  onClick={() => set("image", null)}
                  className="absolute top-2 right-2 bg-black/70 text-white p-1 hover:bg-signal transition-colors"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="mt-2 flex items-center gap-2 w-fit cursor-pointer font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2.5 hover:border-signal hover:text-signal transition-colors" data-testid="community-upload-label">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="community-file-input" />
              </label>
            )}
          </div>

          <button
            data-testid="community-submit-btn"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {saving ? "Sending…" : "Submit to NERO SUN"}
          </button>
        </div>
      )}
    </div>
  );
}
